import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronDown,
  ChevronRight,
  Phone,
  Plus,
  Workflow,
} from "lucide-react";
import { PageHeader } from "../components/layout/PageHeader";
import { OverviewToolbar } from "../components/layout/OverviewToolbar";
import { Modal } from "../components/ui/Modal";
import {
  loadViewMode,
  saveViewMode,
  useFitsMultipleColumns,
  ViewToggle,
  type ViewMode,
} from "../components/layout/ViewToggle";
import {
  applicationsApi,
  type ApplicationListItem,
} from "../api/applicationsApi";
import { HttpError } from "../api/http";

const APPS_CARD_MIN = 280;

type LoadState =
  | { status: "loading" }
  | { status: "ready"; items: ApplicationListItem[] }
  | { status: "error"; message: string };

type StatusFilter = "all" | "active" | "inactive";

const STATUS_OPTIONS: { id: StatusFilter; label: string }[] = [
  { id: "active", label: "Active" },
  { id: "inactive", label: "Inactive" },
  { id: "all", label: "All" },
];

/** Active when the SP returned the literal "Active" stateName. */
function isActiveItem(item: ApplicationListItem): boolean {
  return item.stateName?.toLowerCase() === "active";
}

/**
 * Customer applications overview. Mirrors the destinations / huntgroups
 * overview style: card or row tiles, a search filter, and a status
 * filter so all three overviews share a consistent toolbar. Clicking a
 * tile jumps directly into the Flow Builder for that application's
 * active flow.
 */
export function ApplicationsPage() {
  const [state, setState] = useState<LoadState>({ status: "loading" });
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("active");
  const [view, setView] = useState<ViewMode>(() => loadViewMode("applications"));
  const [modalOpen, setModalOpen] = useState(false);
  const navigate = useNavigate();
  const listRef = useRef<HTMLDivElement>(null);
  const canShowToggle = useFitsMultipleColumns(listRef, APPS_CARD_MIN);

  useEffect(() => {
    let active = true;
    applicationsApi
      .list()
      .then((response) => {
        if (!active) return;
        setState({ status: "ready", items: response.items ?? [] });
      })
      .catch((err: unknown) => {
        if (!active) return;
        const message =
          err instanceof Error ? err.message : "Failed to load applications";
        setState({ status: "error", message });
      });
    return () => {
      active = false;
    };
  }, []);

  const counts = useMemo(() => {
    if (state.status !== "ready")
      return { all: 0, active: 0, inactive: 0 };
    return {
      all: state.items.length,
      active: state.items.filter(isActiveItem).length,
      inactive: state.items.filter((x) => !isActiveItem(x)).length,
    };
  }, [state]);

  const filtered = useMemo(() => {
    if (state.status !== "ready") return [] as ApplicationListItem[];
    const q = search.trim().toLowerCase();
    return state.items
      .filter((x) => {
        if (statusFilter === "active") return isActiveItem(x);
        if (statusFilter === "inactive") return !isActiveItem(x);
        return true;
      })
      .filter((x) => {
        if (!q) return true;
        const hay = [
          x.applicationName ?? "",
          x.stateName ?? "",
          ...(x.dnisNumbers ?? []),
        ]
          .join(" ")
          .toLowerCase();
        return hay.includes(q);
      })
      .slice()
      .sort((a, b) =>
        (a.applicationName ?? "").localeCompare(
          b.applicationName ?? "",
          "en",
          { sensitivity: "base" },
        ),
      );
  }, [state, search, statusFilter]);

  const existingNames = useMemo(() => {
    if (state.status !== "ready") return new Set<string>();
    return new Set(
      state.items.map((it) =>
        (it.applicationName || "").trim().toLowerCase(),
      ),
    );
  }, [state]);

  return (
    <>
      <PageHeader
        title="Applications"
        actions={
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setModalOpen(true)}
            style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
          >
            <Plus size={16} />
            New application
          </button>
        }
      />
      <div className="dashboard">
        {state.status === "loading" && <LoadingState />}
        {state.status === "error" && <ErrorState message={state.message} />}
        {state.status === "ready" && state.items.length === 0 && <EmptyState />}
        {state.status === "ready" && state.items.length > 0 && (
          <>
            <OverviewToolbar
              search={search}
              onSearch={setSearch}
              placeholder="Search by name or DNIS"
              count={{ matched: filtered.length, total: counts.all }}
              trailing={
                <div
                  style={{
                    display: "inline-flex",
                    gap: 8,
                    alignItems: "center",
                  }}
                >
                  <StatusFilterSelect
                    value={statusFilter}
                    counts={counts}
                    onChange={setStatusFilter}
                  />
                  {canShowToggle && (
                    <ViewToggle
                      value={view}
                      onChange={(next) => {
                        setView(next);
                        saveViewMode("applications", next);
                      }}
                    />
                  )}
                </div>
              }
            />
            <div ref={listRef}>
              {filtered.length === 0 ? (
                <FilterEmptyState
                  hasSearch={Boolean(search.trim())}
                  filter={statusFilter}
                />
              ) : view === "card" || !canShowToggle ? (
                <ApplicationsCardGrid
                  items={filtered}
                  onOpen={(id) => navigate(`/applications/${id}`)}
                />
              ) : (
                <ApplicationsRowList
                  items={filtered}
                  onOpen={(id) => navigate(`/applications/${id}`)}
                />
              )}
            </div>
          </>
        )}
      </div>

      <CreateApplicationModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        existingNames={existingNames}
        onCreated={(id) => {
          setModalOpen(false);
          // New applications land on the detail page (not FlowBuilder)
          // so the customer can schedule a number before editing a flow.
          navigate(`/applications/${id}`);
        }}
      />
    </>
  );
}

function CreateApplicationModal({
  open,
  onClose,
  existingNames,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  existingNames: Set<string>;
  onCreated: (id: number) => void;
}) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset between opens so stale input doesn't leak through.
  useEffect(() => {
    if (!open) {
      setName("");
      setBusy(false);
      setError(null);
    }
  }, [open]);

  const trimmed = name.trim();
  const clientDuplicate =
    trimmed.length > 0 && existingNames.has(trimmed.toLowerCase());
  const canSubmit = !busy && trimmed.length > 0;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      const created = await applicationsApi.create(trimmed);
      onCreated(created.applicationId);
    } catch (err) {
      // Server-side uniqueness trumps our client-side prediction: the
      // list can be stale if another admin just added a record.
      if (err instanceof HttpError && err.status === 409) {
        setError("An application with that name already exists.");
      } else {
        const fallback =
          err instanceof Error ? err.message : "Failed to create application";
        const apiDetail =
          err instanceof HttpError && typeof err.data === "object" && err.data
            ? (err.data as { error?: string; detail?: string }).detail ?? null
            : null;
        setError(apiDetail ?? fallback);
      }
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New application"
      busy={busy}
      footer={
        <>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={submit}
            disabled={!canSubmit}
            style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
          >
            <Plus size={16} />
            {busy ? "Creating…" : "Create"}
          </button>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
        style={{ display: "grid", gap: 10 }}
      >
        <label style={{ display: "grid", gap: 4 }}>
          <span
            style={{
              fontSize: "0.8rem",
              color: "var(--portal-muted)",
              fontWeight: 600,
            }}
          >
            Name
          </span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={busy}
            placeholder="e.g. Main menu"
            maxLength={50}
            autoFocus
            style={{
              padding: "8px 12px",
              border: "1px solid var(--portal-border)",
              borderRadius: 6,
              fontSize: "0.95rem",
              fontFamily: "inherit",
              background: "#ffffff",
            }}
          />
        </label>
        {clientDuplicate && !error && (
          <div style={{ fontSize: "0.85rem", color: "#8a6d00" }}>
            An application named "{trimmed}" already exists. Pick a different
            name.
          </div>
        )}
        {error && (
          <div style={{ fontSize: "0.85rem", color: "#c0392b" }}>{error}</div>
        )}
        <button type="submit" style={{ display: "none" }} aria-hidden="true" />
      </form>
    </Modal>
  );
}

function StatusFilterSelect({
  value,
  counts,
  onChange,
}: {
  value: StatusFilter;
  counts: { all: number; active: number; inactive: number };
  onChange: (next: StatusFilter) => void;
}) {
  return (
    <div style={{ position: "relative" }}>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as StatusFilter)}
        style={{
          appearance: "none",
          padding: "6px 32px 6px 12px",
          border: "1px solid var(--portal-border)",
          borderRadius: 999,
          background: "white",
          fontFamily: "inherit",
          fontSize: "0.85rem",
          fontWeight: 600,
          color: "var(--portal-text)",
          cursor: "pointer",
        }}
        aria-label="Filter by status"
      >
        {STATUS_OPTIONS.map((o) => (
          <option key={o.id} value={o.id}>
            {o.label} ({counts[o.id]})
          </option>
        ))}
      </select>
      <ChevronDown
        size={14}
        style={{
          position: "absolute",
          right: 10,
          top: "50%",
          transform: "translateY(-50%)",
          pointerEvents: "none",
          color: "var(--portal-muted)",
        }}
      />
    </div>
  );
}

function FilterEmptyState({
  hasSearch,
  filter,
}: {
  hasSearch: boolean;
  filter: StatusFilter;
}) {
  let message: string;
  if (hasSearch) {
    message = "No applications match this search.";
  } else if (filter === "active") {
    message = "No active applications for this customer.";
  } else if (filter === "inactive") {
    message = "All applications are active.";
  } else {
    message = "No applications.";
  }
  return (
    <div className="tile" style={{ color: "var(--portal-muted)" }}>
      {message}
    </div>
  );
}

function LoadingState() {
  return (
    <div className="tile" style={{ color: "var(--portal-muted)" }}>
      Loading applications…
    </div>
  );
}

function ErrorState({ message }: { message: string }) {
  return (
    <div
      className="tile"
      style={{
        borderColor: "#e74c3c",
        color: "#c0392b",
        background: "#fdecea",
      }}
    >
      <strong>Something went wrong.</strong>
      <div style={{ marginTop: 6, fontSize: "0.9rem" }}>{message}</div>
    </div>
  );
}

function EmptyState() {
  return (
    <section
      className="tile"
      style={{
        maxWidth: 720,
        display: "grid",
        gridTemplateColumns: "56px 1fr",
        gap: 18,
        alignItems: "flex-start",
        padding: 24,
      }}
    >
      <div
        className="tile-row-icon"
        style={{ width: 56, height: 56, borderRadius: 14 }}
        aria-hidden="true"
      >
        <Workflow size={28} />
      </div>
      <div>
        <h3 style={{ marginTop: 0 }}>No applications yet</h3>
        <p style={{ marginTop: 0 }}>
          There are no IVR applications for this customer yet. Add one to
          start designing a flow.
        </p>
      </div>
    </section>
  );
}

function ApplicationsCardGrid({
  items,
  onOpen,
}: {
  items: ApplicationListItem[];
  onOpen: (applicationId: number) => void;
}) {
  return (
    <div
      className="tile-cards"
      style={{ ["--tile-min" as never]: `${APPS_CARD_MIN}px` }}
    >
      {items.map((item) => (
        <button
          key={item.applicationId}
          type="button"
          className="tile tile-link"
          onClick={() => onOpen(item.applicationId)}
          style={{ textAlign: "left", gap: 10, padding: 20 }}
          aria-label={`Open ${item.applicationName}`}
        >
          <div
            style={{
              fontWeight: 700,
              fontSize: "1rem",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={item.applicationName}
          >
            {item.applicationName || "—"}
          </div>
          <div>
            <StateChip stateName={item.stateName} />
          </div>
          <DnisList numbers={item.dnisNumbers} />
          <div
            style={{
              color: "var(--portal-muted)",
              fontSize: "0.8rem",
            }}
          >
            Modified: {formatDate(item.modifiedUtc)}
          </div>
          <div
            style={{
              marginTop: "auto",
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              color: "var(--portal-primary)",
              fontWeight: 600,
              fontSize: "0.85rem",
              gap: 4,
            }}
          >
            Open
            <ChevronRight size={16} />
          </div>
        </button>
      ))}
    </div>
  );
}

function ApplicationsRowList({
  items,
  onOpen,
}: {
  items: ApplicationListItem[];
  onOpen: (applicationId: number) => void;
}) {
  return (
    <div className="tile-rows">
      {items.map((item) => (
        <button
          key={item.applicationId}
          type="button"
          className="tile tile-link"
          onClick={() => onOpen(item.applicationId)}
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: 16,
            padding: "12px 18px",
            textAlign: "left",
          }}
          aria-label={`Open ${item.applicationName}`}
        >
          <div
            style={{
              flex: "2 1 0",
              minWidth: 0,
              fontWeight: 600,
              fontSize: "1rem",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={item.applicationName}
          >
            {item.applicationName || "—"}
          </div>
          <div style={{ flexShrink: 0 }}>
            <StateChip stateName={item.stateName} />
          </div>
          <div
            className="tile-row-detail"
            style={{
              flex: "1 1 0",
              minWidth: 0,
              display: "flex",
              alignItems: "center",
              gap: 6,
              color: "var(--portal-muted)",
              fontSize: "0.9rem",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            <Phone size={14} style={{ flexShrink: 0 }} />
            <span
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={(item.dnisNumbers ?? []).join(", ")}
            >
              {(item.dnisNumbers ?? []).join(", ") || "(no DNIS)"}
            </span>
          </div>
          <span
            className="tile-row-detail"
            style={{
              flexShrink: 0,
              color: "var(--portal-muted)",
              fontSize: "0.85rem",
            }}
            title={item.modifiedUtc ?? undefined}
          >
            {formatDate(item.modifiedUtc)}
          </span>
          <ChevronRight
            size={16}
            style={{ color: "var(--portal-primary)", flexShrink: 0 }}
          />
        </button>
      ))}
    </div>
  );
}

function DnisList({ numbers }: { numbers: string[] }) {
  if (!numbers || numbers.length === 0) return null;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 6,
        color: "var(--portal-muted)",
        fontSize: "0.85rem",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
      title={numbers.join(", ")}
    >
      <Phone size={14} style={{ flexShrink: 0 }} />
      <span
        style={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {numbers.join(", ")}
      </span>
    </div>
  );
}

function StateChip({ stateName }: { stateName: string }) {
  const isActive = stateName?.toLowerCase() === "active";
  const styles: React.CSSProperties = isActive
    ? {
        background: "var(--portal-status-success-soft)",
        color: "var(--portal-status-success-darker)",
        borderColor: "var(--portal-status-success)",
      }
    : {
        background: "#fdecea",
        color: "#c0392b",
        borderColor: "#e74c3c",
      };
  const label = isActive ? "Active" : "Inactive";
  return (
    <span
      style={{
        display: "inline-block",
        padding: "3px 10px",
        borderRadius: 999,
        border: "1px solid",
        fontSize: "0.8rem",
        fontWeight: 600,
        ...styles,
      }}
    >
      {label}
    </span>
  );
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch {
    return "—";
  }
}
